import type {ReactElement} from 'react';
import React, {useState, useRef} from 'react';
import {SearchBar, SearchBarSize} from '@Components/search-bar';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ControlledList} from '@Components/controlled-list';
import {timezoneDropdownId} from '@Components/event-wizard/timezone/timezone.types';
import type {ControlledListItem3} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import type {TimeZone} from 'timezones-list';
import {onEventWizardTimeZoneUpdate} from '@Components/event-wizard/event-wizard-slice';
import {updatePreviewCountdown, updatePreviewDateTime, updatePreviewTimeZone} from '@Components/event-wizard/preview/preview.helper';
import {useDispatch} from 'react-redux';
import {filterTimeZones} from '@Components/event-wizard/timezone/timezone.helper';
import {
  useEventWizardCountdown,
  useEventWizardEndDateTime,
  useEventWizardRecurringData,
  useEventWizardStartDateTime,
  useEventWizardTimeZone,
} from '@Components/event-wizard/event-wizard.hooks';
import {getTzCodeWithShortText} from '@Components/event-wizard/timezone-content/timezone-content.helper';
import styles from './timezone-content.module.scss';
import type {TimezoneContentProps} from './timezone-content.types';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';

export function TimezoneContent({onSelectTimezone}: TimezoneContentProps): ReactElement {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const dispatch = useDispatch();
  const selectedStartDateTime = useEventWizardStartDateTime();
  const selectedEndDateTime = useEventWizardEndDateTime();
  const recurringPattern = useEventWizardRecurringData();
  const selectedTimezone: TimeZone = useEventWizardTimeZone();
  const filteredTimeZones: TimeZone[] = filterTimeZones(searchTerm);
  const isShowCountdown = useEventWizardCountdown();

  const popupRef = useRef<HTMLDivElement | null>(null);
  const {topShadow} = useTopBottomScrollShadow(popupRef);

  const updateTimeZone = (newTimeZone: TimeZone, wasSelected: boolean): void => {
    dispatch(onEventWizardTimeZoneUpdate(newTimeZone));
    updatePreviewCountdown(isShowCountdown ? selectedStartDateTime : undefined, newTimeZone);
    updatePreviewTimeZone(newTimeZone.tzCode);
    updatePreviewDateTime({
      startDateTime: selectedStartDateTime,
      endDateTime: selectedEndDateTime,
      recurringPattern: recurringPattern,
      timeZone: newTimeZone,
    });

    if (onSelectTimezone) {
      onSelectTimezone(newTimeZone, wasSelected);
    }
  };

  const getContentItems = (): ControlledListItem3[] => {
    return filteredTimeZones.map((timeZone) => {
      const timezoneSubtext = timeZone.label.split(' ')[1].replace('GMT', 'UTC');
      const isSelected = selectedTimezone.tzCode === timeZone.tzCode;

      return {
        id: timeZone.tzCode,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: getTzCodeWithShortText(timeZone.tzCode),
        subText: timezoneSubtext,
        isSelected,
        className: styles.listItem,
        onClick(): void {
          updateTimeZone(timeZone, isSelected);
        },
      };
    });
  };

  return (
    <div ref={popupRef} className={styles.contentContainer}>
      <SearchBar
        className={`${styles.searchBar} ${topShadow ? styles.searchScroll : styles.searchNoScroll} bg-neutral-0 spacing-m-b-2`}
        placeholder={window.i18next.t('pmwjs_search')}
        size={SearchBarSize.MEDIUM}
        iconClass={styles.searchIcon}
        iconSize={IconSize.SIZE_ICON_16}
        onInput={setSearchTerm}
        autoFocus
        isSticky
      />
      <ControlledList scrollIntoViewIfSelected id={timezoneDropdownId} items={getContentItems()} />
    </div>
  );
}
